import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "swiper/swiper-bundle.css";
import "flatpickr/dist/flatpickr.css";
import App from "./App.tsx";
import { AppWrapper } from "./components/common/PageMeta.tsx";
import { ThemeProvider } from "./context/ThemeContext.tsx";
import { AuthProvider } from "./context/AuthContext.tsx"; // Import AuthProvider
import { SettingsProvider } from "./context/SettingsContext.tsx"; // Import SettingsProvider
import { CommandSearchProvider } from "./context/CommandSearchContext.tsx"; // Import CommandSearchProvider
import { LegalModalProvider } from "./hooks/useLegalModal.tsx"; // Import LegalModalProvider
import GoogleOAuthProvider from "./components/auth/GoogleOAuthProvider.tsx"; // Import GoogleOAuthProvider
import "./i18n"; // Import i18n configuration
import { silenceThirdPartyWarnings } from "./utils/cleanLogger";
import { safeReload } from "./hooks/useAppUpdater";

// Silenciar warnings de terceros en desarrollo
silenceThirdPartyWarnings();

// Register service worker for PWA functionality (only in production)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');

      // Handle waiting SW (new version ready)
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }
      });

      // Refresh when new SW takes over (guarded against double reload)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        safeReload();
      });
    } catch (error) {
      console.error('PWA: Service Worker registration failed', error);
    }
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider> {/* AuthProvider must be first */}
      <GoogleOAuthProvider> {/* Google OAuth Provider */}
        <ThemeProvider> {/* ThemeProvider needs AuthProvider */}
          <SettingsProvider> {/* Wrap with SettingsProvider */}
            <CommandSearchProvider> {/* Wrap with CommandSearchProvider */}
              <LegalModalProvider> {/* Wrap with LegalModalProvider */}
                <AppWrapper>
                  <App />
                </AppWrapper>
              </LegalModalProvider>
            </CommandSearchProvider>
          </SettingsProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </AuthProvider>
  </StrictMode>,
);
